import {
  useContext,
  createContext,
  useCallback,
  useMemo,
} from 'react';
import type {
  UserSession, Permission, PortalType,
} from '@/types/auth';
import {
  hasPermission,
  hasAnyPermission,
  getPrimaryGroup,
} from '@/lib/permissions';
import type { SignInResult } from '@/lib/cognito-client';

interface AuthContextValue {
  user: UserSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  portalType: PortalType;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
  // New password flow (first login)
  requiresNewPassword: boolean;
  pendingUsername: string | null;
  setNewPassword: (newPassword: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

class AuthContextError extends Error {
  constructor() {
    super('useAuth must be used within an AuthProvider');
    this.name = 'AuthContextError';
  }
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new AuthContextError();
  }
  return context;
}

export function usePermissions() {
  const { user } = useAuth();

  const can = useCallback(
    (permission: Permission): boolean => {
      if (!user) return false;
      return hasPermission(user, permission);
    },
    [user]
  );

  const canAny = useCallback(
    (permissions: Permission[]): boolean => {
      if (!user) return false;
      return hasAnyPermission(user, permissions);
    },
    [user]
  );

  return {
    can,
    canAny,
  };
}

export function useUserRole() {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user) {
      return {
        groups: [],
        primaryGroup: null,
        supplierId: null,
      };
    }
    return {
      groups: user.groups,
      primaryGroup: getPrimaryGroup(user),
      supplierId: user.supplierId,
    };
  }, [user]);
}
